import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { loginChangesStatusbar } from "../../utils/ipadPwa";
import { has, isArray } from "lodash";
import { toast } from "react-toastify";
import moment from "moment";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import pushNotifications from "../../services/pushNotifications";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";
import {
  getStoredAuthToken,
  setStoredAuthToken,
  clearStoredAuthToken,
} from "../../utils/authTokenStorage";
import { SPLASH_LOADER_DURATION_MS } from "../../components/SplashLoader";
import queryClient from "../../queries/queryClient";
import { clearPersistedQueryCache } from "../../queries/persistence";

const REFRESH_INTERVAL_MS = 55 * 60 * 1000;
const RESUME_REFRESH_THROTTLE_MS = 30000;
const SESSION_WATCHDOG_INTERVAL_MS = 30000;
const AUTH_REFRESH_TIMEOUT_MS = 10000;
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;
const BOOT_SPLASH_SESSION_KEY = "neozapp:boot-splash-shown";
const PENDING_LOGIN_WARNINGS_KEY = "neozapp:pending-login-warnings";

const PUBLIC_BOOT_SPLASH_BLOCKLIST = new Set([
  "/login",
  "/signup",
  "/forgetpsw",
  "/termos",
  "/privacidade",
  "/exclusao-dados",
]);

const shouldShowBootSplash = () => {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return false;
  }

  const pathname = window.location?.pathname || "";
  if (PUBLIC_BOOT_SPLASH_BLOCKLIST.has(pathname)) {
    return false;
  }

  const alreadyShown = window.sessionStorage.getItem(BOOT_SPLASH_SESSION_KEY) === "1";
  if (alreadyShown) {
    return false;
  }

  window.sessionStorage.setItem(BOOT_SPLASH_SESSION_KEY, "1");
  return true;
};

const evaluateCompanyDueInfo = (company) => {
  if (!company?.dueDate) {
    return {
      isOverdue: false,
      formatted: "",
      daysUntilDue: null,
    };
  }

  const dueDateMoment = moment(company.dueDate);
  if (!dueDateMoment.isValid()) {
    return {
      isOverdue: false,
      formatted: "",
      daysUntilDue: null,
    };
  }

  const now = moment();
  const daysUntilDue = moment.duration(dueDateMoment.diff(now)).asDays();
  return {
    isOverdue: !now.isBefore(dueDateMoment),
    formatted: dueDateMoment.format("DD/MM/yyyy"),
    daysUntilDue,
  };
};

const enrichUser = (user, dueInfo) => {
  if (!user) {
    return user;
  }

  const info = dueInfo || evaluateCompanyDueInfo(user.company);
  return {
    ...user,
    companyIsOverdue: info.isOverdue,
  };
};

const emitWindowEvent = (type, detail = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(new CustomEvent(type, { detail }));
};

const getRefreshFailureStatus = (errorCode) => {
  if (errorCode === "ERR_SESSION_REVOKED") {
    return "revoked";
  }

  if (["ERR_SESSION_EXPIRED", "ERR_NO_AUTH_TOKEN"].includes(errorCode)) {
    return "expired";
  }

  return "error";
};

const waitForRemainingDuration = async (startedAt, durationMs) => {
  const elapsed = Date.now() - startedAt;
  const remaining = Math.max(0, durationMs - elapsed);

  if (remaining === 0) {
    return;
  }

  await new Promise((resolve) => setTimeout(resolve, remaining));
};

export {
  getStoredAuthToken,
  setStoredAuthToken,
  clearStoredAuthToken,
};

const useAuth = () => {
  const history = useHistory();

  useEffect(() => {
    try {
      const pending = sessionStorage.getItem(PENDING_LOGIN_WARNINGS_KEY);
      if (!pending) return;
      sessionStorage.removeItem(PENDING_LOGIN_WARNINGS_KEY);
      JSON.parse(pending).forEach((warning) => toast.warn(warning));
    } catch (_) { /* Restricted storage just skips the courtesy warnings. */ }
  }, []);
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingScreen, setLoadingScreen] = useState("blocking");
  const [splashVisible, setSplashVisible] = useState(false);
  const [user, setUser] = useState({});
  const [recoveryState, setRecoveryState] = useState("idle");
  const socketManager = useContext(SocketContext);

  const refreshPromiseRef = useRef(null);
  const failedRequestsQueueRef = useRef([]);
  const refreshTimerRef = useRef(null);
  const resumeRefreshThrottleRef = useRef(0);
  const authStateRef = useRef({ isAuth: false });

  const updateSocketAuthToken = useCallback((token) => {
    if (socketManager && typeof socketManager.updateAuthToken === "function") {
      socketManager.updateAuthToken(token);
    }
  }, [socketManager]);

  const resetSocketConnection = useCallback(() => {
    if (socketManager && typeof socketManager.resetConnection === "function") {
      socketManager.resetConnection();
    }
  }, [socketManager]);

  const clearScheduledRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const flushFailedRequestsQueue = useCallback(({ token = null, error = null } = {}) => {
    if (!failedRequestsQueueRef.current.length) {
      return;
    }

    failedRequestsQueueRef.current.forEach((request) => {
      if (token) {
        request.resolve(token);
        return;
      }

      request.reject(error || new Error("Failed to refresh session"));
    });

    failedRequestsQueueRef.current = [];
  }, []);

  const clearLocalAuthState = useCallback(({ preserveLoadingScreen = false } = {}) => {
    clearScheduledRefresh();
    clearStoredAuthToken();
    queryClient.clear();
    clearPersistedQueryCache();
    localStorage.removeItem("companyId");
    localStorage.removeItem("userId");
    api.defaults.headers.Authorization = undefined;
    resetSocketConnection();
    setIsAuth(false);
    setUser({});
    setRecoveryState("idle");
    if (!preserveLoadingScreen) {
      setLoadingScreen("blocking");
    }
    authStateRef.current = { isAuth: false };
    emitWindowEvent("neozapp:auth-cleared");
  }, [clearScheduledRefresh, resetSocketConnection]);

  const notifySessionRevoked = useCallback(() => {
    toast.info(i18n.t("backendErrors.ERR_SESSION_REVOKED"), {
      toastId: "ERR_SESSION_REVOKED",
      autoClose: 3000,
    });
  }, []);

  const failSessionAndRedirect = useCallback((reason) => {
    console.warn(`[auth] ending local session due to ${reason}`);
    if (["ERR_SESSION_REVOKED", "revoked"].includes(String(reason || ""))) {
      notifySessionRevoked();
    }
    clearLocalAuthState();
    setLoading(false);
    setLoadingScreen("blocking");
    if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
      history.replace("/login");
    }
  }, [clearLocalAuthState, history, notifySessionRevoked]);

  const scheduleTokenRefresh = useCallback((delayMs = REFRESH_INTERVAL_MS) => {
    clearScheduledRefresh();

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const refreshHandler = socketManager?.getRefreshHandler?.();
        if (typeof refreshHandler === "function") {
          const result = await refreshHandler({ reason: "timer" });
          if (result?.status === "restored") {
            scheduleTokenRefresh(REFRESH_INTERVAL_MS);
          }
        }
      } catch (_error) {
        console.warn("[auth] scheduled token renewal failed; retry on next activity");
      }
    }, delayMs);
  }, [clearScheduledRefresh, socketManager]);

  const hydrateAuthenticatedUser = useCallback(async ({
    token,
    source = "unknown",
    skipRestoreEvent = false,
  }) => {
    if (!token) {
      throw new Error("Missing token during auth hydration");
    }

    setStoredAuthToken(token);
    api.defaults.headers.Authorization = `Bearer ${token}`;
    updateSocketAuthToken(token);

    const { data: authIdentity } = await api.get("/auth/me");
    const { data: userDetails } = await api.get(`/users/${authIdentity.id}`);
    const enrichedUser = enrichUser(userDetails);

    setIsAuth(true);
    setUser(enrichedUser);
    setRecoveryState("idle");
    authStateRef.current = { isAuth: true };
    localStorage.setItem("userId", authIdentity.id);
    localStorage.setItem("companyId", userDetails.companyId);

    scheduleTokenRefresh(REFRESH_INTERVAL_MS);

    if (!skipRestoreEvent) {
      emitWindowEvent("neozapp:auth-restored", {
        source,
        companyId: userDetails.companyId,
        userId: authIdentity.id,
      });
    }

    return {
      authIdentity,
      userDetails: enrichedUser,
    };
  }, [scheduleTokenRefresh, updateSocketAuthToken]);

  const syncPushAfterAuthRestore = useCallback(async (companyId) => {
    try {
      const result = await pushNotifications.ensureSubscriptionHealthSafe();
      if (result?.ok && result?.healed) {
        console.info("[auth] push subscription healed after auth restore");
      }

      const endpointResult = await pushNotifications.getCurrentEndpointSafe();
      const endpoint = endpointResult?.ok ? endpointResult.endpoint : null;
      if (!endpoint || !companyId) {
        return;
      }

      const socket = socketManager?.getSocket?.(companyId);
      if (socket && typeof socket.emit === "function") {
        socket.emit("pushPresence:update", {
          endpoint,
          state: "foreground",
        });
      }
    } catch (_error) {
      // best-effort push rebind after session restore
    }
  }, [socketManager]);

  const refreshSession = useCallback(async ({
    reason = "unknown",
    force = false,
  } = {}) => {
    if (refreshPromiseRef.current && !force) {
      return refreshPromiseRef.current;
    }

    const currentToken = getStoredAuthToken();
    console.info(`[auth] attempting refresh-first restore (${reason})`, {
      hasLocalToken: Boolean(currentToken),
    });

    setRecoveryState((current) =>
      current === "bootstrapping" ? current : "recovering"
    );

    const refreshPromise = (async () => {
      try {
        const { data } = await api.post(
          "/auth/refresh_token",
          undefined,
          { timeout: AUTH_REFRESH_TIMEOUT_MS }
        );
        const nextToken = data?.token;

        if (!nextToken) {
          flushFailedRequestsQueue({
            error: new Error("Missing token during session refresh")
          });
          return { status: "error" };
        }

        const hydrated = await hydrateAuthenticatedUser({
          token: nextToken,
          source: reason,
        });
        syncPushAfterAuthRestore(hydrated?.userDetails?.companyId);

        console.info(`[auth] session restored successfully (${reason})`);
        flushFailedRequestsQueue({ token: nextToken });
        return {
          status: "restored",
          token: nextToken,
          user: hydrated?.userDetails,
        };
      } catch (error) {
        const responseStatus = error?.response?.status;
        const errorCode = error?.response?.data?.error;

        if (!error?.response) {
          console.warn(`[auth] restore offline or timed out (${reason})`);
          setRecoveryState("offline-recoverable");
          flushFailedRequestsQueue({ error });
          return { status: "offline", error };
        }

        if (responseStatus === 401) {
          const failureStatus = getRefreshFailureStatus(errorCode);
          console.warn(`[auth] refresh rejected (${reason})`, {
            status: failureStatus,
            errorCode,
          });
          flushFailedRequestsQueue({ error });
          return { status: failureStatus, error };
        }

        console.warn(`[auth] refresh failed with backend error (${reason})`, {
          responseStatus,
          errorCode,
        });
        setRecoveryState("failed");
        flushFailedRequestsQueue({ error });
        return { status: "error", error };
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    refreshPromiseRef.current = refreshPromise;
    return refreshPromise;
  }, [flushFailedRequestsQueue, hydrateAuthenticatedUser, syncPushAfterAuthRestore]);

  const completeLogoutCleanup = useCallback(async () => {
    let pushEndpoint = null;

    try {
      const endpointResult = await pushNotifications.getCurrentEndpointSafe();
      if (endpointResult?.ok) {
        pushEndpoint = endpointResult.endpoint;
      } else if (endpointResult?.reason && endpointResult.reason !== "unsupported") {
        console.warn("Falha ao obter endpoint push no logout:", endpointResult);
      }
    } catch (error) {
      console.warn("Falha ao obter endpoint push no logout:", error);
    }

    const logoutConfig = pushEndpoint
      ? { data: { pushEndpoint }, timeout: LOGOUT_REQUEST_TIMEOUT_MS }
      : { timeout: LOGOUT_REQUEST_TIMEOUT_MS };

    try {
      await api.delete("/auth/logout", logoutConfig);
    } catch (error) {
      console.warn("Falha ao notificar backend no logout:", error);
    }

    try {
      const unsubscribeResult = await pushNotifications.unsubscribeByEndpointSafe({
        notifyBackend: false
      });
      if (!unsubscribeResult?.ok && unsubscribeResult?.reason !== "unsupported") {
        console.warn("Falha ao remover subscription push local no logout:", unsubscribeResult);
      }
    } catch (error) {
      console.warn("Falha ao remover subscription push local no logout:", error);
    } finally {
      localStorage.removeItem("cshow");
      clearLocalAuthState({ preserveLoadingScreen: true });
      history.push("/login");
    }
  }, [clearLocalAuthState, history]);

  useEffect(() => {
    authStateRef.current = { isAuth };
  }, [isAuth]);

  useEffect(() => {
    if (socketManager && typeof socketManager.setRefreshHandler === "function") {
      socketManager.setRefreshHandler(refreshSession);
    }

    return () => {
      if (socketManager && typeof socketManager.clearRefreshHandler === "function") {
        socketManager.clearRefreshHandler(refreshSession);
      }
    };
  }, [refreshSession, socketManager]);

  useEffect(() => {
    const requestInterceptorId = api.interceptors.request.use(
      (config) => {
        const token = getStoredAuthToken();
        if (token) {
          if (!config.headers) {
            config.headers = {};
          }
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptorId = api.interceptors.response.use(
      (response) => {
        scheduleTokenRefresh(REFRESH_INTERVAL_MS);
        return response;
      },
      async (error) => {
        const originalRequest = error?.config || {};
        const errorCode = error?.response?.data?.error;

        const shouldBypassAuthRefresh =
          error?.response?.status === 403 &&
          ["ERR_NO_QUEUE_ACCESS", "ERR_TICKET_ASSIGNED_OTHER_USER"].includes(errorCode);

        if (shouldBypassAuthRefresh) {
          return Promise.reject(error);
        }

        const isRefreshTokenCall =
          typeof originalRequest?.url === "string" &&
          originalRequest.url.includes("/auth/refresh_token");

        if (isRefreshTokenCall) {
          return Promise.reject(error);
        }

        const isLogoutCall =
          typeof originalRequest?.url === "string" &&
          originalRequest.url.includes("/auth/logout");

        if (isLogoutCall) {
          return Promise.reject(error);
        }

        const isInvalidAccessToken =
          error?.response?.status === 403 &&
          errorCode === "ERR_INVALID_ACCESS_TOKEN";

        if (isInvalidAccessToken && !originalRequest._retry) {
          if (refreshPromiseRef.current) {
            return new Promise((resolve, reject) => {
              failedRequestsQueueRef.current.push({ resolve, reject });
            })
              .then((token) => {
                if (!originalRequest.headers) {
                  originalRequest.headers = {};
                }
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return api(originalRequest);
              })
              .catch((err) => Promise.reject(err));
          }

          originalRequest._retry = true;

          try {
            const refreshResult = await refreshSession({ reason: "axios-invalid-access-token" });

            if (refreshResult?.status === "restored" && refreshResult.token) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`;
              return api(originalRequest);
            }

            if (["expired", "revoked"].includes(refreshResult?.status)) {
              failSessionAndRedirect(refreshResult.status);
            }

            return Promise.reject(error);
          } catch (refreshError) {
            flushFailedRequestsQueue({ error: refreshError });
            return Promise.reject(refreshError);
          }
        }

        const shouldForceLogout =
          error?.response?.status === 401 &&
          ["ERR_SESSION_EXPIRED", "ERR_SESSION_REVOKED", "ERR_NO_AUTH_TOKEN"].includes(errorCode);

        if (shouldForceLogout) {
          failSessionAndRedirect(errorCode);
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptorId);
      api.interceptors.response.eject(responseInterceptorId);
      clearScheduledRefresh();
    };
  }, [clearScheduledRefresh, failSessionAndRedirect, flushFailedRequestsQueue, refreshSession, scheduleTokenRefresh]);

  useEffect(() => {
    const validateActiveSession = async (source) => {
      const token = getStoredAuthToken();
      const shouldRecover = !token || !authStateRef.current.isAuth;

      if (shouldRecover) {
        const refreshResult = await refreshSession({
          reason: `${source}:restore`,
        });

        if (refreshResult?.status === "restored") {
          setLoading(false);
          scheduleTokenRefresh(REFRESH_INTERVAL_MS);
          return;
        }

        if (["expired", "revoked"].includes(refreshResult?.status)) {
          failSessionAndRedirect(refreshResult.status);
        }
        return;
      }

      try {
        await api.get("/auth/me", { timeout: AUTH_REFRESH_TIMEOUT_MS });
        setRecoveryState("idle");
        scheduleTokenRefresh(REFRESH_INTERVAL_MS);
      } catch (error) {
        if (!error?.response) {
          console.warn(`[auth] active session check offline or timed out (${source})`);
          setRecoveryState("offline-recoverable");
          return;
        }

        const failureStatus = getRefreshFailureStatus(error?.response?.data?.error);
        if (["expired", "revoked"].includes(failureStatus) && authStateRef.current.isAuth) {
          failSessionAndRedirect(failureStatus);
        }
      }
    };

    const maybeValidateAfterResume = async (source) => {
      if (document.visibilityState === "hidden") {
        return;
      }

      const now = Date.now();
      if (now - resumeRefreshThrottleRef.current < RESUME_REFRESH_THROTTLE_MS) {
        return;
      }
      resumeRefreshThrottleRef.current = now;

      validateActiveSession(source);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        maybeValidateAfterResume("visibilitychange");
      }
    };

    const onOnline = () => {
      maybeValidateAfterResume("online");
    };

    const onPageShow = () => {
      maybeValidateAfterResume("pageshow");
    };

    const onFocus = () => {
      maybeValidateAfterResume("focus");
    };

    const onUserActivity = () => {
      maybeValidateAfterResume("user-activity");
    };

    const watchdogTimer = window.setInterval(() => {
      maybeValidateAfterResume("foreground-watchdog");
    }, SESSION_WATCHDOG_INTERVAL_MS);
    const passiveListenerOptions = { passive: true };

    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener("resume", onPageShow);
    document.addEventListener("touchstart", onUserActivity, passiveListenerOptions);
    document.addEventListener("pointerdown", onUserActivity, passiveListenerOptions);
    window.addEventListener("online", onOnline);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("focus", onFocus);

    return () => {
      window.clearInterval(watchdogTimer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("resume", onPageShow);
      document.removeEventListener("touchstart", onUserActivity, passiveListenerOptions);
      document.removeEventListener("pointerdown", onUserActivity, passiveListenerOptions);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("focus", onFocus);
    };
  }, [failSessionAndRedirect, refreshSession, scheduleTokenRefresh]);

  useEffect(() => {
    const bootstrapAuth = async () => {
      const loadingStartedAt = Date.now();
      const token = getStoredAuthToken();
      const useBootSplash = shouldShowBootSplash();
      let authSucceeded = false;
      console.info("[auth] bootstrap start", { hasLocalToken: Boolean(token) });
      setRecoveryState("bootstrapping");
      setLoadingScreen(useBootSplash ? "splash" : "blocking");
      setSplashVisible(useBootSplash);

      try {
        if (token) {
          try {
            await hydrateAuthenticatedUser({
              token,
              source: "bootstrap-local-token",
              skipRestoreEvent: true,
            });
            console.info("[auth] bootstrap completed with local token");
            authSucceeded = true;
            return;
          } catch (_bootstrapErr) {
            console.warn("[auth] local token bootstrap failed, falling back to refresh-first");
          }
        }

        console.info("[auth] bootstrap refresh-first restore", {
          withoutLocalToken: !token,
        });

        const refreshResult = await refreshSession({
          reason: token ? "bootstrap-refresh-fallback" : "bootstrap-no-local-token",
          force: true,
        });

        if (refreshResult?.status === "restored") {
          authSucceeded = true;
          return;
        }

        if (["expired", "revoked"].includes(refreshResult?.status)) {
          clearLocalAuthState({ preserveLoadingScreen: useBootSplash });
          if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
            history.replace("/login");
          }
          return;
        }

        if (refreshResult?.status === "offline") {
          console.warn("[auth] bootstrap offline-recoverable; redirecting to login");
          setRecoveryState("offline-recoverable");
          clearLocalAuthState({ preserveLoadingScreen: useBootSplash });
          if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
            history.replace("/login");
          }
          return;
        }

        clearLocalAuthState({ preserveLoadingScreen: useBootSplash });
        if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
          history.replace("/login");
        }
      } catch (error) {
        if (!error?.response) {
          console.warn("[auth] bootstrap network failure; redirecting to login");
          setRecoveryState("offline-recoverable");
          clearLocalAuthState({ preserveLoadingScreen: useBootSplash });
          if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
            history.replace("/login");
          }
        } else {
          toastError(error);
          clearLocalAuthState({ preserveLoadingScreen: useBootSplash });
          if (!PUBLIC_BOOT_SPLASH_BLOCKLIST.has(window.location.pathname)) {
            history.replace("/login");
          }
        }
      } finally {
        if (useBootSplash && authSucceeded) {
          await waitForRemainingDuration(loadingStartedAt, SPLASH_LOADER_DURATION_MS);
        }
        setSplashVisible(false);
        setLoading(false);
        setLoadingScreen("blocking");
      }
    };

    bootstrapAuth();

    return () => {
      clearScheduledRefresh();
    };
  }, [clearLocalAuthState, clearScheduledRefresh, history, hydrateAuthenticatedUser, refreshSession]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    if (companyId) {
      const socket = socketManager.getSocket(companyId);
      const authEvent = `company-${companyId}-auth`;

      const handleAuthEvent = (data) => {
        const eventUserId = data?.user?.id;
        const currentUserId = user?.id || localStorage.getItem("userId");
        const isCurrentUser =
          eventUserId &&
          currentUserId &&
          String(eventUserId) === String(currentUserId);

        if (data?.action !== "sessionRevoked" || !isCurrentUser) {
          return;
        }

        failSessionAndRedirect(data.error || "ERR_SESSION_REVOKED");
      };

      socket.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          setUser(enrichUser(data.user));
        }
      });

      socket.on(authEvent, handleAuthEvent);

      socket.on("permissionsChanged", () => {
        socket.emit("refreshUserData");
      });

      socket.on("userDataRefreshed", (freshData) => {
        if (freshData && freshData.id === user.id) {
          toast.info(i18n.t("auth.toasts.permissionsUpdated") || "Suas permissões foram atualizadas. Recarregando...", {
            autoClose: 2000,
          });

          setUser(prev => ({
            ...prev,
            profile: freshData.profile,
            queues: freshData.queues,
            allTicket: freshData.allTicket,
            allowNoQueue: freshData.allowNoQueue
          }));

          setTimeout(() => {
            window.location.reload();
          }, 2500);
        }
      });

      return () => {
        socket.off(`company-${companyId}-user`);
        socket.off(authEvent, handleAuthEvent);
        socket.off("permissionsChanged");
        socket.off("userDataRefreshed");
      };
    }
  }, [failSessionAndRedirect, socketManager, user.id]);

  const handleLogin = async (userData) => {
    const loadingStartedAt = Date.now();
    let loginSucceeded = false;
    // On an installed iPad or iPhone the login stays on screen until the new document, so the page
    // and the statusbar change together instead of a white splash under a green bar.
    const opensNewDocument = loginChangesStatusbar();
    if (!opensNewDocument) {
      setLoading(true);
      setLoadingScreen("splash");
      setSplashVisible(true);
    }

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { companyId, id, company },
      } = data;

      if (has(company, "settings") && isArray(company.settings)) {
        const setting = company.settings.find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      moment.locale("pt-br");
      const dueInfo = evaluateCompanyDueInfo(company);
      const enrichedUser = enrichUser(data.user, dueInfo);

      setStoredAuthToken(data.token);
      localStorage.setItem("companyId", companyId);
      localStorage.setItem("userId", id);
      localStorage.setItem("companyDueDate", dueInfo.formatted);

      const companyStatus = enrichedUser?.company?.status;
      const companyBillingStatus = String(enrichedUser?.company?.billingStatus || "");
      const billingBlocked = ["SUSPENDED_OVERDUE", "CANCELLED_BY_ADMIN"].includes(
        companyBillingStatus
      );
      const loginTarget = companyStatus === false || billingBlocked ? "/financeiro" : "/tickets";
      const loginWarnings = [];
      if (companyStatus === false) {
        loginWarnings.push(i18n.t("auth.companyInactive"));
      }
      if (dueInfo.isOverdue) {
        loginWarnings.push(i18n.t("auth.companyOverdue"));
      } else if (
        dueInfo.daysUntilDue !== null &&
        dueInfo.daysUntilDue >= 0 &&
        Math.round(dueInfo.daysUntilDue) < 5
      ) {
        const roundedDays = Math.round(dueInfo.daysUntilDue);
        loginWarnings.push(
          `Sua assinatura vence em ${roundedDays} ${roundedDays === 1 ? "dia" : "dias"} `
        );
      }

      if (opensNewDocument) {
        try {
          sessionStorage.setItem(PENDING_LOGIN_WARNINGS_KEY, JSON.stringify(loginWarnings));
        } catch (_) { /* The warnings are a courtesy; login goes on without them. */ }
        window.location.assign(loginTarget);
        return;
      }

      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      updateSocketAuthToken(data.token);
      setUser(enrichedUser);
      setIsAuth(true);
      setRecoveryState("idle");
      authStateRef.current = { isAuth: true };

      scheduleTokenRefresh(REFRESH_INTERVAL_MS);
      emitWindowEvent("neozapp:auth-restored", {
        source: "login",
        companyId,
        userId: id,
      });

      syncPushAfterAuthRestore(companyId);

      toast.success(i18n.t("auth.toasts.success"));
      loginWarnings.forEach((warning) => toast.warn(warning));

      history.push(loginTarget);
      loginSucceeded = true;
    } catch (err) {
      setLoadingScreen("blocking");
      toastError(err);
    } finally {
      if (loginSucceeded) {
        await waitForRemainingDuration(loadingStartedAt, SPLASH_LOADER_DURATION_MS);
      }
      setSplashVisible(false);
      setLoading(false);
      setLoadingScreen("blocking");
    }
  };

  const handleLogout = async () => {
    const loadingStartedAt = Date.now();
    setLoading(true);
    setLoadingScreen("splash");
    setSplashVisible(true);

    try {
      await completeLogoutCleanup();
    } catch (err) {
      toastError(err);
    } finally {
      await waitForRemainingDuration(loadingStartedAt, SPLASH_LOADER_DURATION_MS);
      setSplashVisible(false);
      setLoading(false);
      setLoadingScreen("blocking");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (err) {
      toastError(err);
    }
  };

  return {
    isAuth,
    user,
    loading,
    loadingScreen,
    splashVisible,
    recoveryState,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    refreshSession,
  };
};

export default useAuth;
